// Open Haunt in the zone framework (docs/build/zones.md): main.ts imports this module for its side effects. It
// registers the zone's own section kinds (zones/open-haunt/zone.json "sections"), its 3D hero (run in the sandboxed
// preview frame) and its Home portal. The modules are the same ones the standalone /haunt page draws (page.ts).
//
// Section kinds are global, so each handler draws only for this zone and hands any other zone to whatever was
// registered before it (the events or assets owners may register "event" or "assets" for their own zones).

import { h } from "../../dom.ts";
import { startScenePreview } from "../../preview-frame.ts";
import { registerHeroScene, registerPortal, registerSection, sectionRenderer, type SectionContext, type SectionRender } from "../registry.ts";
import { gateFromBrowser, qualityFor, wantsLiveHero } from "./hero.ts";
import { clockBar, creators, loadHauntData, nightBand, packSection, questCard, seasonStrip, showcase, startClock } from "./page.ts";
import { hauntPortal } from "./portal.ts";
import { zoneNow } from "./season.ts";
import { ensureZoneStyle } from "./style.ts";

export const ZONE_ID = "open-haunt";

type Ticks = ((t: number) => void)[];

/** Register `kind` for this zone only, keeping any earlier renderer for other zones. */
function own(kind: string, render: (ctx: SectionContext, now: number, ticks: Ticks) => HTMLElement | null | Promise<HTMLElement | null>): void {
  const before: SectionRender | undefined = sectionRenderer(kind);
  registerSection(kind, (ctx) => {
    if (ctx.zone.id !== ZONE_ID) return before ? before(ctx) : null;
    ensureZoneStyle();
    const now = zoneNow();
    const ticks: Ticks = [];
    const wrap = (el: HTMLElement | null) => {
      if (!el) return null;
      const box = h("div", { class: "oh oh-in-zone" }, el);
      if (ticks.length) startClock(box, now, ticks);
      return box;
    };
    const out = render(ctx, now, ticks);
    return out instanceof Promise ? out.then(wrap) : wrap(out);
  });
}

own("clock", async (_ctx, now, ticks) => {
  const d = await loadHauntData();
  const { clock, actions } = clockBar(now, ticks, d.live);
  return h("div", { class: "oh-clockbar" }, clock, actions);
});
own("timeline", (_ctx, now) => seasonStrip(now));
own("quest", async () => {
  const d = await loadHauntData();
  return questCard(d.quest, d.games, d.c);
});
own("showcase", async (_ctx, now) => {
  const d = await loadHauntData();
  return showcase(d.shown, d.boosted, d.live, now);
});
own("assets", async (ctx) => (ctx.arg === "halloween" ? packSection((await loadHauntData()).c) : null));
own("event", async (ctx, now, ticks) => (ctx.arg === "plaza-night" ? nightBand((await loadHauntData()).night, now, ticks) : null));
own("creators", async () => {
  const d = await loadHauntData();
  return creators(d.live ? d.games : [], d.c);
});

// the 3D hero: the pack's scene in the sandboxed preview frame (three.js never loads in the page). Reduced motion,
// Save-Data and low-power phones keep the poster: the mount rejects and the shell leaves it in place.
registerHeroScene(ZONE_ID, async () => (host, { reducedMotion }) => {
  const gate = { ...gateFromBrowser(), reducedMotion: reducedMotion || gateFromBrowser().reducedMotion };
  const force = new URLSearchParams(location.search).get("hero") === "3d";
  if (!force && !wantsLiveHero(gate)) return Promise.reject(new Error("poster only on this device"));
  return new Promise((resolve) => {
    startScenePreview(host, "open-haunt", qualityFor(gate), () => resolve({ dispose: () => host.replaceChildren() }));
  });
});

// the zone's own clock (the dev-only ?hauntNow included), not the shell's
registerPortal(ZONE_ID, () => hauntPortal(zoneNow()));
